import { Divider, SkeletonText, Space, Text } from '@billy/design-system'
import { ReactElement, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useSegment } from '../../../../hooks'
import { useContactBalance, useMultiplePayableSubjects } from '../../../paymentReconciliationModal/contexts'
import { getTypeFromPayableSubject } from '../../../paymentReconciliationModal/utils'
import { PaymentDateInput } from '../PaymentDateInput'
import { PrepaymentDifferenceLine } from '../PrepaymentDifferenceLine'
import { usePrepaymentModalDisplayValues } from './hooks/usePrepaymentModalDisplayValues'
import * as Styled from './styles'

export const PrepaymentModalBody = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { contactName, displayedContactBalanceAmount } = usePrepaymentModalDisplayValues()
  const { contactBalance } = useContactBalance()
  const { initialSubject } = useMultiplePayableSubjects()
  const subjectType = getTypeFromPayableSubject(initialSubject)

  const billBalanceAfterPrepayment = useMemo(() => {
    const billBalance = initialSubject?.balance
    const contactBalanceAmount = contactBalance?.amount
    if (!billBalance || !contactBalanceAmount) {
      return null
    }
    const balanceDifference = billBalance - contactBalanceAmount
    return billBalance > contactBalanceAmount ? balanceDifference : 0
  }, [initialSubject?.balance, contactBalance?.amount])

  const currency = useMemo(() => initialSubject?.currencyId || '', [initialSubject?.currencyId])

  useEffect(() => {
    track('xxx - expenses - Prepayment modal opened')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Space />
      <Styled.PrePaymentModalBodyContainer>
        <Styled.PrePaymentModalBodyColumn flex={7}>
          {!!displayedContactBalanceAmount && !!contactName ? (
            <Text>
              <Trans
                i18nKey="bill.payment_reconcile.prepayment.you_have_credit"
                values={{
                  amount: displayedContactBalanceAmount,
                  name: contactName,
                }}
                components={{ strong: <strong /> }}
              />
            </Text>
          ) : (
            <SkeletonText />
          )}
          <Space />
          <Text>{t('bill.payment_reconcile.prepayment.chose_payment_date')}</Text>
        </Styled.PrePaymentModalBodyColumn>
        <Divider direction="vertical" />
        <Styled.PrePaymentModalBodyColumn flex={3}>
          <PaymentDateInput />
        </Styled.PrePaymentModalBodyColumn>
      </Styled.PrePaymentModalBodyContainer>

      <Space />
      {!!billBalanceAfterPrepayment && (
        <>
          <Space size="s12" />
          <PrepaymentDifferenceLine
            difference={billBalanceAfterPrepayment}
            currency={currency}
            subjectType={subjectType}
          />
        </>
      )}
    </>
  )
}
